import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { HttpClientModule } from "@angular/common/http";
import { MatIconModule } from "@angular/material/icon";
import { IconsModule } from "../shared/icons/icons.module";

import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatAutocompleteModule } from "@angular/material/autocomplete";

import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from "@angular/material-moment-adapter";

import { NgxSliderModule } from "@angular-slider/ngx-slider";
import { DBConfig, NgxIndexedDBModule } from "ngx-indexed-db";

import { ToastrModule } from "ngx-toastr";

import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
  ScrollingModule,
} from "@angular/cdk/scrolling";

import { MatProgressBarModule } from "@angular/material/progress-bar";
import { DomService } from "./services/internal-services/dom.service";
import { OverlayDialogComponent } from "../dialog-components/overlay-dialog/overlay-dialog.component";
import { PhoenixViewerContainerComponent } from "./components/containers/phoenix-viewer-container/phoenix-viewer-container.component";
import { PhoenixViewerComponent } from "./components/viewers/phoenix-viewer/phoenix-viewer.component";
import { PhoenixBrightnessContrastComponent } from "./components/brightness-contrast/phoenix-brightness-contrast.component";
import { DeveloperToolsComponent } from "./components/developer-tools/developer-tools.component";
import { PhoenixDownloadImageryComponent } from "./components/download-imagery/phoenix-download-imagery.component";
import { PhoenixDualViewComponent } from "./components/dual-view/phoenix-dual-view.component";
import { PhoenixSearchManagerComponent } from "./components/search-manager/phoenix-search-manager.component";
import { CustomToastComponent } from "./components/toast-notifications/custom-toast/custom-toast.component";
import { ProjectsDiscreteSelectorComponent } from "./components/timelines/projects/projects-discrete-selector/projects-discrete-selector.component";
import { PhoenixViewerService } from "./services/internal-services/viewer-services/phoenix-viewer.service";
import { LibCollectionViewerComponent } from "./pages/phoenix-viewer/phoenix-viewer-page.component";
import { FeatureInfoComponent } from "./components/feature-info/feature-info.component";
import { ProjectTimelineComponent } from "./components/timelines/projects/projects-timeline/project-timeline.component";
import { LayerItemComponent } from "./components/layer-item/layer-item.component";
import { CollectionManagerComponent } from "./components/layer-managers/collection-manager/collection-manager.component";
import { ProjectLayerItemComponent } from "./components/project-layer-item/project-layer-item.component";
import { ImagesPanelComponent } from "./components/developer-tools/images-panel/images-panel.component";
import { AoiPanelComponent } from "./components/developer-tools/aoi-panel/aoi-panel.component";
import { OverlayLayerItemComponent } from "./components/overlay-layer-item/overlay-layer-item.component";
import { StatusBarComponent } from "./components/status-bar/status-bar.component";
import { PhoenixPropertyLoaderComponent } from "./components/property-loader/phoenix-property-loader.component";
import { PropertyInfoTableComponent } from "./components/property-info/property-info-table/property-info-table.component";
import { UserDataComponent } from "./components/user-data/user-data.component";
import { UserDataListComponent } from "./components/user-data/user-data-list/user-data-list.component";
import { UserLayerDataComponent } from "./components/user-data/user-layer-data/user-layer-data.component";
import { FeatureDataComponent } from "./components/user-data/feature-data/feature-data.component";
import { FeatureDetailComponent } from "./components/user-data/feature-detail/feature-detail.component";
import { ProjectsTimelineFilterComponent } from "./components/timelines/projects/projects-timeline-filter/projects-timeline-filter.component";
import { DummyProjectsTimelineComponent } from "./components/timelines/projects/dummy-projects-timeline/dummy-projects-timeline.component";
import { CustomPinComponent } from "./components/custom-pin/custom-pin.component";
import { MatSliderModule } from "@angular/material/slider";
import { VisionToastComponent } from "./components/toast-notifications/vision-toast/vision-toast.component";
import { MeasurementActionsComponent } from "./components/measurement/measurement-actions/measurement-actions.component";
import { CollectionTimelineComponent } from "./components/timelines/collections/collections-timeline/collection-timeline.component";
import { InfraredSelectorComponent } from "./components/infrared-selector/infrared-selector.component";
import { SwipeViewerComponent } from "./components/swipe-viewer/swipe-viewer.component";
import { DummyCollectionsTimelineComponent } from "./components/timelines/collections/dummy-collections-timeline/dummy-collections-timeline.component";

import { ExportGeotiffComponent } from "./components/export-geotiff/export-geotiff.component";
import { MeasurementToolbarComponent } from "./components/measurement/measurement-toolbar/measurement-toolbar.component";
import { CollectionControllerComponent } from "./components/collection-controller/collection-controller.component";
import { LocationInfoComponent } from "./components/location-info/location-info.component";
import { MultilayerSwipeViewerComponent } from "./components/swipe-viewer/multilayer/multilayer-swipe-viewer.component";
import { MeasurementInfoComponent } from "./components/measurement/measurement-info/measurement-info.component";
import { CollectionsDiscreteSelectorComponent } from "./components/timelines/collections/collections-discrete-selector/collections-discrete-selector.component";
import { CollectionTimelineFilterComponent } from "./components/timelines/collections/collections-timeline-filter/collection-timeline-filter.component";
import { CoveragePanelComponent } from "./components/developer-tools/coverage-panel/coverage-panel.component";

import { DialogComponentsModule } from "../dialog-components/dialog-components.module";
import { SharedComponentsModule } from "../shared-components/shared-components.module";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatRadioModule } from "@angular/material/radio";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MapContainerComponent } from "./components/containers/map-container/map-container.component";
import { CompoundViewerComponent } from "./components/viewers/compound-viewer/compound-viewer.component";
import { ZoomComponent } from "./components/zoom/zoom.component";
import { SearchEnhancedResultsComponent } from "../toolbars-components/search-enhanced-results/search-enhanced-results.component";
import { SbsTimelineComponent } from "./components/timelines/base/sbs-timeline/sbs-timeline.component";
import { FilterComponent } from "./components/filter/filter.component";
import { ImageInfoComponent } from "./components/image-info/image-info.component";
import { ScreenshotComponent } from "./components/screenshot/screenshot.component";
import { SearchBarComponent } from "./components/search-bar/search-bar.component";
import { PropertyLocationInfoComponent } from "./components/property-location-info/property-location-info.component";
import { ColorSwatchPickerComponent } from "../shared-components/color-swatch-picker/color-swatch-picker.component";
import { StyleLegendPanelComponent } from "../shared-components/style-legend-panel/style-legend-panel.component";
import { ScaleBarComponent } from "./components/scale-bar/scale-bar.component";
import { OverlayManagerComponent } from "./components/layer-managers/overlay-manager/overlay-manager.component";
import { PropertiesLocationsManagerComponent } from "./components/properties-locations-manager/properties-locations-manager.component";
import { LayerManagerWidgetComponent } from "./components/layer-managers/layer-manager-widget/layer-manager-widget.component";
import { SearchHeatmapConfigComponent } from "../toolbars-components/search-heatmap-config/search-heatmap-config.component";
import { AiChatToolbarComponent } from "../toolbars-components/ai-chat-toolbar/ai-chat-toolbar.component";
import { CustomElementsToolbarComponent } from "../toolbars-components/custom-elements-toolbar/custom-elements-toolbar.component";
import { ColorPickerDirective } from "ngx-color-picker";
import { OverlayModule } from "@angular/cdk/overlay";
import { MapControllerComponent } from "./components/map-controller/map-controller.component";
import { CompassControllerComponent } from "./components/map-controller/compass-controller/compass-controller.component";
import { CompactMapControllerComponent } from "./components/compact-map-controller/compact-map-controller.component";
import { ChatContainerComponent, ChatPanelHdrComponent } from "../ai-components/chat";
import { ChatOverlayResizeDirective } from "../shared-components/directives/chat-overlay-resize.directive";
import { PropertyMapControlsComponent } from "./components/property-map-controls/property-map-controls.component";
import { PropertyOverviewComponent } from "./components/property-overview/property-overview.component";
import { DamageOverviewComponent } from "./components/damage-overview/damage-overview.component";
import { LayerFeaturePanelComponent } from "../shared-components/feature-table/layer-feature-panel.component";
import { FeatureDetailPanelComponent } from "../shared-components/feature-detail-panel/feature-detail-panel.component";
import { VlmLayerPanelComponent } from "../shared-components/vlm-layer-panel/vlm-layer-panel.component";
import { PipelineEmbeddingsPanelComponent } from "../shared-components/pipeline-embeddings-panel/pipeline-embeddings-panel.component";
import { PipelineAoiPanelComponent } from "../shared-components/pipeline-aoi-panel/pipeline-aoi-panel.component";

const dbConfig: DBConfig = {
  name: "vexcel-data",
  version: 4,
  objectStoresMeta: [
    {
      store: "user-layers",
      storeConfig: { keyPath: "id", autoIncrement: false },
      storeSchema: [{ name: "type", keypath: "type", options: { unique: false } }],
    },
    {
      store: "user-properties",
      storeConfig: { keyPath: "requestId", autoIncrement: false },
      storeSchema: [{ name: "type", keypath: "type", options: { unique: false } }],
    },
    {
      store: "user-properties-damage-assessments",
      storeConfig: { keyPath: "requestId", autoIncrement: false },
      storeSchema: [{ name: "type", keypath: "type", options: { unique: false } }],
    },
  ],
};

@NgModule({
  declarations: [
    LibCollectionViewerComponent,
    FeatureInfoComponent,
    CustomToastComponent,
    VisionToastComponent,
    StatusBarComponent,
    PhoenixViewerComponent,
    PhoenixViewerContainerComponent,
    CollectionManagerComponent,
    CollectionControllerComponent,
    MapControllerComponent,
    PhoenixBrightnessContrastComponent,
    PhoenixDownloadImageryComponent,
    PhoenixDualViewComponent,
    ImageInfoComponent,
    MeasurementActionsComponent,
    MeasurementToolbarComponent,
    ScreenshotComponent,
    PhoenixSearchManagerComponent,
    SearchBarComponent,
    ZoomComponent,
    OverlayDialogComponent,
    OverlayDialogComponent,
    LocationInfoComponent,
    FeatureInfoComponent,
    ProjectsDiscreteSelectorComponent,
    CollectionsDiscreteSelectorComponent,
    PropertyLocationInfoComponent,
    ProjectTimelineComponent,
    InfraredSelectorComponent,
    MeasurementInfoComponent,
    FilterComponent,
    ScaleBarComponent,
    LayerItemComponent,
    ProjectLayerItemComponent,
    OverlayManagerComponent,
    ScaleBarComponent,
    SwipeViewerComponent,
    AoiPanelComponent,
    ImagesPanelComponent,
    OverlayLayerItemComponent,
    PropertiesLocationsManagerComponent,
    PhoenixPropertyLoaderComponent,
    ExportGeotiffComponent,
    UserDataComponent,
    UserDataListComponent,
    UserLayerDataComponent,
    FeatureDataComponent,
    FeatureDetailComponent,
    PropertyInfoTableComponent,
    ProjectsTimelineFilterComponent,
    LayerManagerWidgetComponent,
    DummyProjectsTimelineComponent,
    CustomPinComponent,
    VisionToastComponent,
    DeveloperToolsComponent,
    CollectionTimelineComponent,
    DummyCollectionsTimelineComponent,
    CollectionTimelineFilterComponent,
    SbsTimelineComponent,
    MultilayerSwipeViewerComponent,
    CoveragePanelComponent,
    MapContainerComponent,
    CompoundViewerComponent,
    PropertyMapControlsComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    SharedComponentsModule,
    DialogComponentsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatIconModule,
    IconsModule,
    MatFormFieldModule,
    MatDividerModule,
    MatExpansionModule,
    MatToolbarModule,
    MatMomentDateModule,
    MatTooltipModule,
    MatSortModule,
    MatButtonToggleModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatRadioModule,
    MatCheckboxModule,
    MatMenuModule,
    MatButtonModule,
    MatInputModule,
    MatCardModule,
    MatFormFieldModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatOptionModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTabsModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatTableModule,
    MatSliderModule,
    NgxSliderModule,
    HttpClientModule,
    NgxIndexedDBModule.forRoot(dbConfig),
    ScrollingModule,
    ToastrModule.forRoot({ positionClass: "inline" }),
    MatProgressBarModule,
    MatSliderModule,
    SearchEnhancedResultsComponent,
    SearchHeatmapConfigComponent,
    AiChatToolbarComponent,
    CustomElementsToolbarComponent,
    ColorPickerDirective,
    ColorSwatchPickerComponent,
    StyleLegendPanelComponent,
    OverlayModule,
    CompassControllerComponent,
    CompactMapControllerComponent,
    PropertyOverviewComponent,
    DamageOverviewComponent,
    ChatContainerComponent,
    ChatPanelHdrComponent,
    ChatOverlayResizeDirective,
    LayerFeaturePanelComponent,
    FeatureDetailPanelComponent,
    VlmLayerPanelComponent,
    PipelineEmbeddingsPanelComponent,
    PipelineAoiPanelComponent,
  ],
  exports: [
    LibCollectionViewerComponent,
    MapContainerComponent,
    SearchBarComponent,
    MapControllerComponent,
    LayerManagerWidgetComponent,
  ],
  providers: [
    DomService,
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true },
    },
    PhoenixViewerService,
  ],
})
export class ViewerComponentsModule {}
